import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Card, Row, Col, InputGroup, Alert } from 'react-bootstrap';
import { Calendar, Users, ShoppingCart, Save, ArrowLeft, DollarSign, Hash, Percent } from 'lucide-react';
import { getOrderById } from '../../api/orders';
import { createInvoice, getInvoiceById, updateInvoice } from '../../api/invoices';
import { formatCurrency } from '../../utils/formatters';
import Loading from '../common/Loading';
import { getAllPartnerBytype, getAllSuppliers } from '../../api/partner';
import { getOrdersByPartnerExt } from '../../api/orders';

const InvoiceForm = ({ onCancel, onSuccess }) => {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [clients, setClients] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    
    const [formData, setFormData] = useState({
        clientId: '',
        orderId: '',
        invoiceNumber: '',
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        discountType: 'fixed', // Type de remise: 'fixed' ou 'percent'
        discountAmount: '0',
        discountPercent: '0',
        notes: '',
        paymentTerms: '0', // Par défaut 30 jours
        status: 'PENDING',
        // Nouveaux champs pour le paiement partiel
        paidAmount: '0',
        paidDate: ''
    });

    // États pour les montants calculés
    const [calculatedAmounts, setCalculatedAmounts] = useState({
        totalHT: 0,
        totalAfterDiscount: 0,
        discountValue: 0,
        TVA: 0,
        totalTTC: 0,
        remainingAmount: 0 // Montant restant à payer
    });

    // Charger les clients au chargement du composant
    useEffect(() => {
        fetchClients();
        
        // Si on est en mode édition, charger les données de la facture
        if (isEditMode) {
            fetchInvoiceDetails();
        }
    }, [id]);

    // Calculer la date d'échéance basée sur la date d'émission et les conditions de paiement
    useEffect(() => {
        if (formData.issueDate && formData.paymentTerms) {
            const issueDate = new Date(formData.issueDate);
            const dueDate = new Date(issueDate);
            dueDate.setDate(dueDate.getDate() + parseInt(formData.paymentTerms));
            
            setFormData(prev => ({
                ...prev,
                dueDate: dueDate.toISOString().split('T')[0]
            }));
        }
    }, [formData.issueDate, formData.paymentTerms]);

    // Calculer les montants lorsque les données pertinentes changent
    useEffect(() => {
        calculateAmounts();
    }, [
        selectedOrder, 
        formData.discountAmount, 
        formData.discountPercent, 
        formData.discountType, 
        selectedClient, 
        formData.paidAmount]);

    // Mettre à jour le statut automatiquement selon le montant payé
    useEffect(() => {
        updateStatusBasedOnPayment();
    }, [calculatedAmounts.totalTTC, formData.paidAmount]);

    const fetchClients = async () => {
        try {
            const clientsList = await getAllSuppliers();
            setClients(clientsList);
        } catch (err) {
            setError("Impossible de charger la liste des clients");
            console.error(err);
        }
    };

    const fetchInvoiceDetails = async () => {
        setLoading(true);
        try {
            const invoiceData = await getInvoiceById(id);
            
            // Charger les détails de la commande associée
            if (invoiceData.orderId) {
                const orderData = await getOrderById(invoiceData.orderId);
                setSelectedOrder(orderData);
            }

            // Charger les détails du client
            if (invoiceData.client && invoiceData.client.id) {
                const clientData = clients.find(c => c.id === invoiceData.client.id);
                setSelectedClient(clientData);
            }

            // Déterminer le type de remise
            const discountType =
            invoiceData.discountPercent > 0 ? "percent" : "fixed";

            
            setFormData({
                clientId: invoiceData.client.id,
                orderId: invoiceData.orderId || '',
                invoiceNumber: invoiceData.invoiceNumber,
                issueDate: invoiceData.issueDate.split('T')[0],
                dueDate: invoiceData.dueDate.split('T')[0],
                discountType: discountType,
                discountAmount: invoiceData.discountAmount || "0",
                discountPercent: invoiceData.discountPercent || "0",
                notes: invoiceData.notes || '',
                paymentTerms: invoiceData.paymentTerms.toString(),
                status: invoiceData.status,
                paidAmount: invoiceData.paidAmount?.toString() || '0',
                paidDate: invoiceData.paidDate ? invoiceData.paidDate.split('T')[0] : ''
            });
        } catch (err) {
            setError("Impossible de charger les détails de la facture");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClientChange = async (e) => {
        const clientId = e.target.value;
        setFormData(prev => ({
            ...prev,
            clientId,
            orderId: '' // Réinitialiser l'ID de commande lors du changement de client
        }));
        setSelectedOrder(null);
        
        // Récupérer les détails du client pour vérifier s'il est assujetti à la TVA
        if (clientId) {
            const selectedClientData = clients.find(c => c.id === clientId);
            setSelectedClient(selectedClientData);
        } else {
            setSelectedClient(null);
        }
    };

    const [orders, setOrders] = useState([]);

    useEffect(() => {
        const fetchOrders = async () => {
            if (!formData.clientId) {
                setOrders([]);
                return;
            }

            try {
                const response = await getOrdersByPartnerExt(formData.clientId);
                setOrders(response);
            } catch (error) {
                console.error("Erreur lors du chargement des commandes :", error);
                setOrders([]);
            }
        };

        fetchOrders();
    }, [formData.clientId]);

    const handleOrderSelect = async (e) => {
        const orderId = e.target.value;
        if (!orderId) {
            setSelectedOrder(null);
            setFormData(prev => ({ ...prev, orderId: '' }));
            return;
        }

        try {
            const orderData = await getOrderById(orderId);
            setSelectedOrder(orderData);
            setFormData(prev => ({ ...prev, orderId }));
        } catch (err) {
            setError("Impossible de charger les détails de la commande");
            console.error(err);
        }
    };

    const handleDiscountTypeChange = (e) => {
    const discountType = e.target.value;
    setFormData((prev) => ({
      ...prev,
      discountType,
      // Réinitialiser les valeurs lors du changement de type de remise
      discountAmount: discountType === "fixed" ? prev.discountAmount : "0",
      discountPercent: discountType === "percent" ? prev.discountPercent : "0",
    }));
  };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePaidAmountChange = (e) => {
        const { value } = e.target;
        const paidAmount = parseFloat(value) || 0;
        
        // Vérifier que le montant payé ne dépasse pas le montant total TTC
        if (paidAmount > calculatedAmounts.totalTTC) {
            setError("Le montant payé ne peut pas dépasser le montant total TTC");
            return;
        } else {
            setError('');
        }
        
        setFormData(prev => ({ ...prev, paidAmount: value }));
    };

    const updateStatusBasedOnPayment = () => {
        if (!calculatedAmounts.totalTTC) return;

        const paidAmount = parseFloat(formData.paidAmount) || 0;
        const totalTTC = calculatedAmounts.totalTTC;

        let newStatus = formData.status;
        let newPaidDate = formData.paidDate;

        if (paidAmount === 0) {
            newStatus = 'PENDING';
            newPaidDate = '';
        } else if (paidAmount > 0 && paidAmount < totalTTC) {
            newStatus = 'PART_PAY';
            newPaidDate = paidAmount > 0 && !formData.paidDate ? new Date().toISOString().split('T')[0] : formData.paidDate;
        } else if (paidAmount >= totalTTC) {
            newStatus = 'PAID';
            newPaidDate = new Date().toISOString().split('T')[0];
        }

        if (newStatus !== formData.status || newPaidDate !== formData.paidDate) {
            setFormData(prev => ({
                ...prev,
                status: newStatus,
                paidDate: newPaidDate
            }));
        }
    };

    const calculateAmounts = () => {
        if (!selectedOrder) {
            setCalculatedAmounts({
                totalHT: 0,
                totalAfterDiscount: 0,
                discountValue: 0,
                TVA: 0,
                totalTTC: 0,
                remainingAmount: 0
            });
            return;
        }

        const totalHT = parseFloat(selectedOrder.totalAmount);
        let discountValue = 0;
        
        // Calculer la valeur de la remise selon le type
        if (formData.discountType === 'fixed') {
            discountValue = parseFloat(formData.discountAmount) || 0;
        } else {
            // Calculer la remise en pourcentage
            const discountPercent = parseFloat(formData.discountPercent) || 0;
            discountValue = (totalHT * discountPercent) / 100;
        }

        // Vérifier que la remise ne dépasse pas le montant HT
        if (discountValue > totalHT) {
            setError("La remise ne peut pas dépasser le montant HT");
            discountValue = totalHT; // Limiter la remise au montant HT
        } else {
            // Effacer l'erreur si elle existe
            if (error === "La remise ne peut pas dépasser le montant HT") {
                setError('');
            }
        }

        const totalAfterDiscount = totalHT - discountValue;
        
        // Calculer la TVA si le client est assujetti (18%)
        let TVA = 0;
        if (selectedClient && selectedClient.vatApplicable) {
            TVA = totalAfterDiscount * 0.18;
        }

        const totalTTC = totalAfterDiscount + TVA;
        const paidAmount = parseFloat(formData.paidAmount) || 0;
        const remainingAmount = Math.max(0, totalTTC - paidAmount);

        setCalculatedAmounts({
            totalHT,
            totalAfterDiscount,
            discountValue,
            TVA,
            totalTTC,
            remainingAmount
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        setSuccess('');

        try {
            // Calculer la remise en fonction du type
            let discountValue = 0;
            if (formData.discountType === 'fixed') {
                discountValue = parseFloat(formData.discountAmount) || 0;
            } else {
                const discountPercent = parseFloat(formData.discountPercent) || 0;
                discountValue = (selectedOrder.totalAmount * discountPercent) / 100;
            }

            // Vérifier que la remise ne dépasse pas le montant HT
            if (discountValue > selectedOrder.totalAmount) {
                throw new Error("La remise ne peut pas dépasser le montant HT");
            }

            // Vérifier que le montant payé ne dépasse pas le montant total TTC
            const paidAmount = parseFloat(formData.paidAmount) || 0;
            if (paidAmount > calculatedAmounts.totalTTC) {
                throw new Error("Le montant payé ne peut pas dépasser le montant total TTC");
            }

            const invoiceData = {
                ...formData,
                totalAmount: selectedOrder ? selectedOrder.totalAmount : 0,
                discountAmount: formData.discountType === 'fixed' ? parseFloat(formData.discountAmount) || 0 : discountValue,
                discountPercent: formData.discountType === 'percent' ? parseFloat(formData.discountPercent) || 0 : 0,
                paidAmount: paidAmount,
                paidDate: formData.paidDate || null
            };

            if (isEditMode) {
                await updateInvoice(id, invoiceData);
                setSuccess("Facture mise à jour avec succès");
            } else {
                await createInvoice(invoiceData);
                setSuccess("Facture créée avec succès");
                // Réinitialiser le formulaire après création
                setFormData({
                    clientId: '',
                    orderId: '',
                    invoiceNumber: '',
                    issueDate: new Date().toISOString().split('T')[0],
                    dueDate: '',
                    notes: '',
                    paymentTerms: '30',
                    status: 'PENDING',
                    paidAmount: '0',
                    paidDate: ''
                });
                setSelectedOrder(null);
                setSelectedClient(null);
            }
            
            // Rediriger vers la liste des factures après 2 secondes
            setTimeout(() => {
                onSuccess();
            }, 2000);
        } catch (err) {
            setError(err.message || "Une erreur est survenue lors de l'enregistrement de la facture");
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusVariant = (status) => {
        switch (status) {
            case 'PENDING': return 'warning';
            case 'PART_PAY': return 'info';
            case 'PAID': return 'success';
            case 'OVERDUE': return 'danger';
            case 'CANCELLED': return 'secondary';
            default: return 'secondary';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'PENDING': return 'En attente de paiement';
            case 'PART_PAY': return 'Partiellement payée';
            case 'PAID': return 'Payée';
            case 'OVERDUE': return 'En retard';
            case 'CANCELLED': return 'Annulée';
            default: return status;
        }
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <Row className="align-items-center">
                    <Col>
                        <h5 className="mb-0">{isEditMode ? 'Modifier la facture' : 'Créer une nouvelle facture'}</h5>
                    </Col>
                    <Col xs="auto">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => onCancel()}
                        >
                            <ArrowLeft size={16} className="me-1" />
                            Retour
                        </Button>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}
                
                <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="clientId">
                                <Form.Label>Client</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><Users size={16} /></InputGroup.Text>
                                    <Form.Select
                                        name="clientId"
                                        value={formData.clientId}
                                        onChange={handleClientChange}
                                        required
                                    >
                                        <option value="">Sélectionner un client</option>
                                        {clients.map(client => (
                                            <option key={client.id} value={client.id}>
                                                {client.name} {client.vatApplicable ? "(TVA applicable)" : ""}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="orderId">
                                <Form.Label>Commande associée</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><ShoppingCart size={16} /></InputGroup.Text>
                                    <Form.Select
                                        name="orderId"
                                        value={formData.orderId}
                                        onChange={handleOrderSelect}
                                        required
                                        disabled={!formData.clientId}
                                    >
                                       <option value="">Sélectionner une commande</option> 
                                            {orders
                                            .filter(order => order.status === 'CONFIRMED' || order.status === 'DELIVERED') // filtre uniquement les commandes confirmées
                                            .map(order => (
                                                <option key={order.id} value={order.id}>
                                                {order.orderNumber} - {formatCurrency(order.totalAmount)}
                                                </option>
                                            ))}

                                    </Form.Select>
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Les commandes disponibles seront affichées après la sélection d'un client
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    {selectedOrder && (
                        <Card className="mb-3 bg-light">
                            <Card.Body>
                                <h6>Détails de la commande et facturation</h6>
                                <Row>
                                    <Col md={3}>
                                        <small className="text-muted">Numéro de commande</small>
                                        <p>{selectedOrder.orderNumber}</p>
                                    </Col>
                                    <Col md={3}>
                                        <small className="text-muted">Date de commande</small>
                                        <p>{new Date(selectedOrder.orderDate).toLocaleDateString()}</p>
                                    </Col>
                                    <Col md={3}>
                                        <small className="text-muted">Montant HT</small>
                                        <p>{formatCurrency(calculatedAmounts.totalHT)}</p>
                                    </Col>
                                    <Col md={3}>
                                    <Form.Group>
                                    <Form.Label>Type de remise</Form.Label>
                                    <Form.Select
                                        name="discountType"
                                        value={formData.discountType}
                                        onChange={handleDiscountTypeChange}
                                    >
                                        <option value="fixed">Montant fixe</option>
                                        <option value="percent">Pourcentage</option>
                                    </Form.Select>
                                    </Form.Group>
                                </Col>
                                    <Col md={3}>
                                        <small className="text-muted">Montant TTC</small>
                                        <p className="fw-bold text-primary">{formatCurrency(calculatedAmounts.totalTTC)}</p>
                                    </Col>
                                </Row>

                                <Row className="mt-3">
                                <Col md={6}>
                                    {formData.discountType === "fixed" ? (
                                    <Form.Group controlId="discountAmount">
                                        <Form.Label>Remise (montant)</Form.Label>
                                        <InputGroup>
                                        <InputGroup.Text>
                                            <Hash size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            name="discountAmount"
                                            onWheel={(e) => e.target.blur()}
                                            value={formData.discountAmount}
                                            onChange={handleInputChange}
                                        />
                                        </InputGroup>
                                    </Form.Group>
                                    ) : (
                                    <Form.Group controlId="discountPercent">
                                        <Form.Label>Remise (pourcentage)</Form.Label>
                                        <InputGroup>
                                        <InputGroup.Text>
                                            <Percent size={16} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            name="discountPercent"
                                            value={formData.discountPercent}
                                            onChange={handleInputChange}
                                        />
                                        <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                    )}
                                </Col>
                                <Col md={6}>
                                    {calculatedAmounts.discountValue > 0 && (
                                    <div className="mt-4">
                                        <p className="text-info">
                                        Remise appliquée:{" "}
                                        {formatCurrency(calculatedAmounts.discountValue)}
                                        {formData.discountType === "percent" &&
                                            ` (${formData.discountPercent}%)`}
                                        </p>
                                    </div>
                                    )}
                                </Col>
                                </Row>

                                 {/* Affichage des calculs de montants */}
                <hr />
                <h6>Récapitulatif financier</h6>
                <Row className="mt-3">
                  <Col md={3}>
                    <small className="text-muted">Montant HT</small>
                    <p>{formatCurrency(calculatedAmounts.totalHT)}</p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">Après remise</small>
                    <p>
                      {formatCurrency(calculatedAmounts.totalAfterDiscount)}
                    </p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">TVA (18%)</small>
                    <p>{formatCurrency(calculatedAmounts.TVA)}</p>
                    {(!selectedClient?.vatApplicable ||
                      !selectedOrder?.vatApplicable) && (
                      <small className="text-muted d-block">
                        {!selectedClient?.vatApplicable
                          ? "Client non assujetti à la TVA"
                          : "Commande exonérée de TVA"}
                      </small>
                    )}
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">Montant TTC</small>
                    <p className="fw-bold">
                      {formatCurrency(calculatedAmounts.totalTTC)}
                    </p>
                  </Col>
                </Row>
                                
                                {/* Section des paiements */}
                                <hr />
                                <Row>
                                    <Col md={4}>
                                        <small className="text-muted">Montant payé</small>
                                        <p className="fw-bold text-success">{formatCurrency(parseFloat(formData.paidAmount) || 0)}</p>
                                    </Col>
                                    <Col md={4}>
                                        <small className="text-muted">Montant restant</small>
                                        <p className="fw-bold text-warning">{formatCurrency(calculatedAmounts.remainingAmount)}</p>
                                    </Col>
                                    <Col md={4}>
                                        <small className="text-muted">Statut</small>
                                        <p>
                                            <span className={`badge bg-${getStatusVariant(formData.status)}`}>
                                                {getStatusText(formData.status)}
                                            </span>
                                        </p>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="issueDate">
                                <Form.Label>Date d'émission</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><Calendar size={16} /></InputGroup.Text>
                                    <Form.Control
                                        type="date"
                                        name="issueDate"
                                        value={formData.issueDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="dueDate">
                                <Form.Label>Date d'échéance</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><Calendar size={16} /></InputGroup.Text>
                                    <Form.Control
                                        type="date"
                                        name="dueDate"
                                        value={formData.dueDate}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Section des paiements */}
                    <Card className="mb-3 border-info">
                        <Card.Header className="bg-info text-white">
                            <h6 className="mb-0">Informations de paiement</h6>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group controlId="paidAmount">
                                        <Form.Label>Montant payé</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text><DollarSign size={16} /></InputGroup.Text>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max={calculatedAmounts.totalTTC}
                                                name="paidAmount"
                                                value={formData.paidAmount}
                                                onWheel={(e) => e.target.blur()}
                                                onChange={handlePaidAmountChange}
                                                placeholder="0.00"
                                            />
                                        </InputGroup>
                                        <Form.Text className="text-muted">
                                            Maximum: {formatCurrency(calculatedAmounts.totalTTC)}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="paidDate">
                                        <Form.Label>Date de paiement</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text><Calendar size={16} /></InputGroup.Text>
                                            <Form.Control
                                                type="date"
                                                name="paidDate"
                                                value={formData.paidDate}
                                                onChange={handleInputChange}
                                                disabled={parseFloat(formData.paidAmount) === 0}
                                            />
                                        </InputGroup>
                                        <Form.Text className="text-muted">
                                            {parseFloat(formData.paidAmount) === 0 ? "Date automatique si montant > 0" : ""}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Row className="mb-3">
                        <Col md={12}>
                            <Form.Group controlId="notes">
                                <Form.Label>Notes / Commentaires</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Notes ou commentaires additionnels pour cette facture..."
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end">
                        <Button 
                            variant="secondary" 
                            className="me-2"
                            onClick={() => onCancel()}
                            disabled={submitting}
                        >
                            Annuler
                        </Button>
                        <Button 
                            type="submit" 
                            variant="primary"
                            disabled={submitting || !formData.clientId || !formData.orderId}
                        >
                            <Save size={16} className="me-1" />
                            {submitting ? 'Enregistrement...' : isEditMode ? 'Modifier' : 'Créer'}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default InvoiceForm;